/**
 * profitReport — a DRE do Orçamigo: Receitas → Lucro Bruto → Lucro Líquido.
 *
 * Fora do componente de propósito, como `paymentsReport` e `stockReport`: tudo
 * aqui é função pura e testável sem renderizar nada (o recharts nem renderiza em
 * jsdom, e dupla contagem passa por `tsc` sem reclamar).
 *
 * ─── AS DUAS BASES DE DATA (a decisão que evita o defeito do concorrente) ────
 *
 * O financeiro é **CAIXA** (`paid_date`/`due_date`: quando o dinheiro entrou ou
 * vence). O CMV é **COMPETÊNCIA** (a data do DOCUMENTO que consumiu o material).
 * São eixos diferentes, e misturá-los sem dizer produz exatamente o defeito que
 * se vê no concorrente: o Fluxo de Caixa mostra despesas `R$ 0,00` e a aba Lucro
 * mostra `Custo de Produtos R$ 52,00` no MESMO mês, sem uma palavra de
 * explicação — e o usuário conclui que um dos dois está quebrado.
 *
 * Aqui a base do lado do caixa é escolha EXPLÍCITA do usuário
 * ({@link PaymentDateBasis}, o mesmo tipo do Relatório de Pagamentos) e a tela
 * rotula qual está ativa. A base do CMV é a data do documento, **sempre**, e a
 * tela também diz isso. Nenhum número aparece sem dizer de onde veio.
 *
 * ─── QUE DOCUMENTOS ENTRAM NO CMV ───────────────────────────────────────────
 *
 * Um documento entra quando está COMPROMETIDO — quer dizer, quando o material
 * já foi (ou está sendo) consumido:
 *   - orçamento: `approved` ou `completed`;
 *   - ordem de serviço: `in_progress` ou `completed`.
 * Pendente e cancelado ficam de fora: podem nunca acontecer.
 *
 * **A OS entra no CMV** (DECISÃO do grupo 5) porque ela é módulo GRÁTIS, pode
 * existir sem orçamento e seus itens carregam `product_id` — deixá-la fora
 * subestimaria o custo e faria o lucro parecer maior do que é. Mas uma OS criada
 * A PARTIR de um orçamento (`quote_id` preenchido) é **pulada**: o custo dela já
 * está no orçamento de origem, e contar os dois dobraria o CMV. Quantas foram
 * puladas é DEVOLVIDO e mostrado na tela — nunca um corte silencioso.
 *
 * ─── AUSÊNCIA DE CUSTO NUNCA É ZERO (armadilha #61) ─────────────────────────
 *
 * `unit_cost` é nullable de propósito. Somar `Number(null) === 0` pelos itens
 * sem custo daria um CMV menor que a realidade e um lucro inflado, sem nenhum
 * erro na tela — o pior resultado possível num número de dinheiro. Então os
 * itens sem custo são **contados à parte** ({@link ProfitReport.missingCostCount})
 * e a tela avisa quantos ficaram de fora.
 *
 * ─── A DESPESA COBRÁVEL NÃO PODE CONTAR DUAS VEZES ──────────────────────────
 *
 * Uma despesa cobrável é receita (a linha materializada no orçamento, que o
 * cliente paga) E despesa (o lançamento). Isso é correto: o resultado é margem
 * ZERO para ela, que é a verdade de um repasse. Por isso ela tem bloco PRÓPRIO
 * na decomposição, separada das despesas operacionais.
 *
 * O que não pode acontecer é ela entrar **também** no CMV. A linha materializada
 * tem `unit_cost` null e é reconhecível por `finance_entry_id`, então é
 * descartada antes da conta — inclusive da contagem de "sem custo", onde
 * apareceria como um alarme falso.
 */
import { roundMoney } from '@/lib/pricing';
import { basisDate, type IsoDateRange, type PaymentDateBasis } from '@/lib/paymentsReport';
import { todayIsoDate } from '@/lib/financeStatus';
import type { FinanceEntryWithStatus } from '@/types/database';

// ===== Que documentos entram =====

/** Orçamento comprometido: o material foi (ou está sendo) consumido. */
export const COGS_QUOTE_STATUSES = ['approved', 'completed'] as const;
/** OS comprometida. `pending`/`cancelled` podem nunca acontecer. */
export const COGS_SERVICE_ORDER_STATUSES = ['in_progress', 'completed'] as const;

export const COGS_BASIS_LABEL = 'Pela data do orçamento ou da ordem de serviço';
export const COGS_SCOPE_LABEL =
  'Orçamentos aprovados ou concluídos e ordens de serviço em andamento ou concluídas.';

export interface ProfitSettings {
  quoteCogsStatuses: string[];
  serviceOrderCogsStatuses: string[];
  /**
   * null = todas as despesas não cobradas, preservando o cálculo anterior.
   * [] = nenhuma categoria selecionada.
   */
  otherExpenseCategoryIds: string[] | null;
  includeUncategorizedOtherExpenses: boolean;
}

/** Não mudar sem migração: estes valores são o DRE anterior, byte a byte. */
export const DEFAULT_PROFIT_SETTINGS: ProfitSettings = {
  quoteCogsStatuses: [...COGS_QUOTE_STATUSES],
  serviceOrderCogsStatuses: [...COGS_SERVICE_ORDER_STATUSES],
  otherExpenseCategoryIds: null,
  includeUncategorizedOtherExpenses: true,
};

export function profitSettingsFromRow(
  row: Record<string, unknown> | null | undefined,
): ProfitSettings {
  const stringArray = (value: unknown, fallback: readonly string[]): string[] =>
    Array.isArray(value) && value.every((item) => typeof item === 'string')
      ? [...value]
      : [...fallback];

  const rawCategoryIds = row?.other_expense_category_ids;
  return {
    quoteCogsStatuses: stringArray(
      row?.quote_cogs_statuses,
      DEFAULT_PROFIT_SETTINGS.quoteCogsStatuses,
    ),
    serviceOrderCogsStatuses: stringArray(
      row?.service_order_cogs_statuses,
      DEFAULT_PROFIT_SETTINGS.serviceOrderCogsStatuses,
    ),
    otherExpenseCategoryIds:
      rawCategoryIds === null || rawCategoryIds === undefined
        ? null
        : stringArray(rawCategoryIds, []),
    includeUncategorizedOtherExpenses:
      typeof row?.include_uncategorized_other_expenses === 'boolean'
        ? row.include_uncategorized_other_expenses
        : DEFAULT_PROFIT_SETTINGS.includeUncategorizedOtherExpenses,
  };
}

export function toProfitSettingsColumns(settings: ProfitSettings) {
  return {
    quote_cogs_statuses: [...settings.quoteCogsStatuses],
    service_order_cogs_statuses: [...settings.serviceOrderCogsStatuses],
    other_expense_category_ids:
      settings.otherExpenseCategoryIds === null
        ? null
        : [...settings.otherExpenseCategoryIds],
    include_uncategorized_other_expenses: settings.includeUncategorizedOtherExpenses,
  };
}

/** Item de documento, no mínimo que a conta precisa. */
export interface CogsItem {
  quantity?: number | string | null;
  /** `null` = custo não informado. NUNCA tratar como zero. */
  unit_cost?: number | string | null;
  /** Preenchido = linha de despesa cobrável (repasse), não mercadoria. */
  finance_entry_id?: string | null;
}

/** Documento normalizado — o hook converte `quotes`/`service_orders` para cá. */
export interface CogsDocument {
  kind: 'quote' | 'service_order';
  id: string;
  /** Data de COMPETÊNCIA, 'YYYY-MM-DD'. Quote: `approved_at ?? created_at`. OS: `completed_at ?? created_at`. */
  date: string | null;
  status: string | null;
  /** Só para OS: o orçamento de origem. Preenchido ⇒ o custo já está lá. */
  originQuoteId?: string | null;
  items: readonly CogsItem[];
}

function money(raw: unknown): number {
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Custo UNITÁRIO informado da linha. `null`/vazio/negativo = ausência; zero é
 * custo legítimo (item doado) e é preservado.
 *
 * Exportada porque o Relatório por Item (`itemsReport.ts`) precisa da MESMA
 * definição de "tem custo?" que o CMV usa — se cada módulo decidisse sozinho, a
 * aba Itens e a aba Lucro divergiriam sobre quantos itens estão sem custo, e
 * nenhuma das duas pareceria errada.
 */
export function cogsItemUnitCost(item: CogsItem): number | null {
  const raw = item?.unit_cost;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** Custo da LINHA (quantidade × custo unitário), em centavos. `null` = sem custo. */
export function cogsItemLineCost(item: CogsItem): number | null {
  const unit = cogsItemUnitCost(item);
  if (unit === null) return null;
  return roundMoney(money(item?.quantity) * unit);
}

/**
 * A linha é um REPASSE (despesa cobrável materializada), não mercadoria.
 *
 * Fica fora do CMV **e** fora da contagem de "sem custo", onde seria alarme
 * falso: ela tem `unit_cost` null de propósito. Ver `billableExpense.ts`.
 */
export function isPassthroughLine(item: CogsItem): boolean {
  return Boolean(item?.finance_entry_id);
}

function inIsoRange(date: string | null | undefined, range: IsoDateRange): boolean {
  if (!date) return false;
  if (range.from && date < range.from) return false;
  if (range.to && date > range.to) return false;
  return true;
}

function qualifies(doc: CogsDocument, settings: ProfitSettings): boolean {
  const statuses: readonly string[] =
    doc.kind === 'quote'
      ? settings.quoteCogsStatuses
      : settings.serviceOrderCogsStatuses;
  return statuses.includes(String(doc.status ?? ''));
}

// ===== CMV =====

export interface CogsResult {
  /** Custo somado — só dos itens que TÊM custo informado. */
  total: number;
  /** Quantos itens entraram na soma. */
  costedItemCount: number;
  /**
   * Itens de documento contado que ficaram FORA por não ter custo. É este número
   * que impede o lucro de parecer melhor do que é sem ninguém perceber.
   */
  missingCostCount: number;
  /** Documentos contados (para a tela dizer sobre o que a conta foi feita). */
  documentCount: number;
  /** OS puladas por virem de um orçamento (o custo já está lá). Nunca silencioso. */
  skippedFromQuoteCount: number;
}

/**
 * Custo das mercadorias/serviços vendidos no período, pela data do DOCUMENTO.
 *
 * Repare que não há `basis` aqui: CMV é competência e ponto. A escolha de base
 * do usuário vale para o lado do caixa (receitas e despesas), e a tela diz isso.
 */
export function computeCogs(
  documents: readonly CogsDocument[],
  range: IsoDateRange,
  settings: ProfitSettings = DEFAULT_PROFIT_SETTINGS,
): CogsResult {
  let total = 0;
  let costedItemCount = 0;
  let missingCostCount = 0;
  let documentCount = 0;
  let skippedFromQuoteCount = 0;

  for (const doc of documents) {
    if (!qualifies(doc, settings)) continue;
    if (!inIsoRange(doc.date, range)) continue;

    // A OS nascida de um orçamento não repete o custo dele.
    if (doc.kind === 'service_order' && doc.originQuoteId) {
      skippedFromQuoteCount += 1;
      continue;
    }

    documentCount += 1;

    for (const item of doc.items ?? []) {
      // Repasse (despesa cobrável materializada) não é mercadoria: fora do CMV
      // e fora da contagem de "sem custo", onde seria alarme falso.
      if (isPassthroughLine(item)) continue;

      const lineCost = cogsItemLineCost(item);
      if (lineCost === null) {
        missingCostCount += 1;
        continue;
      }
      total = roundMoney(total + lineCost);
      costedItemCount += 1;
    }
  }

  return { total, costedItemCount, missingCostCount, documentCount, skippedFromQuoteCount };
}

// ===== DRE =====

export interface ProfitReport {
  /** Entradas do financeiro no período, pela base escolhida. */
  revenue: number;
  revenueCount: number;
  /** Custo das mercadorias/serviços vendidos — competência, não caixa. */
  cogs: number;
  /** Saídas marcadas como COBRÁVEIS: repasse ao cliente, margem zero. */
  billableExpenses: number;
  billableExpensesCount: number;
  /** revenue − cogs − billableExpenses. */
  grossProfit: number;
  /** Saídas operacionais (tudo que não é repasse). */
  otherExpenses: number;
  otherExpensesCount: number;
  /** grossProfit − otherExpenses. */
  netProfit: number;
  /** Margem líquida em % da receita. `null` quando não houve receita (nunca NaN). */
  netMargin: number | null;
  /** Detalhe do CMV, inclusive o que ficou de fora por falta de custo. */
  cogsDetail: CogsResult;
  /** `true` quando não há nada no período — a tela mostra estado vazio, não zeros. */
  isEmpty: boolean;
}

function amountOf(entry: FinanceEntryWithStatus): number {
  const n = Number(entry.amount ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * A DRE inteira.
 *
 * Só lançamentos efetivamente QUITADOS entram (`status === 'paid'`) quando a
 * base é `paid`; na base `due` entra o previsto do período, quitado ou não.
 * Cancelado nunca entra: não é dinheiro.
 */
export function computeProfitReport(
  entries: readonly FinanceEntryWithStatus[],
  documents: readonly CogsDocument[],
  range: IsoDateRange,
  basis: PaymentDateBasis,
  settings: ProfitSettings = DEFAULT_PROFIT_SETTINGS,
): ProfitReport {
  let revenue = 0;
  let revenueCount = 0;
  let billableExpenses = 0;
  let billableExpensesCount = 0;
  let otherExpenses = 0;
  let otherExpensesCount = 0;

  for (const entry of entries) {
    if (entry.status === 'canceled') continue;
    if (!inIsoRange(basisDate(entry as FinanceEntryWithStatus, basis), range)) continue;
    // Na base "realizado" só conta o que foi de fato quitado. Na base "previsto"
    // o título em aberto é justamente o que se quer enxergar.
    if (basis === 'paid' && entry.status !== 'paid') continue;

    const value = amountOf(entry);
    if (entry.kind === 'income') {
      revenue = roundMoney(revenue + value);
      revenueCount += 1;
    } else if (entry.billable) {
      billableExpenses = roundMoney(billableExpenses + value);
      billableExpensesCount += 1;
    } else if (
      settings.otherExpenseCategoryIds === null
      || (
        entry.category_id !== null
        && settings.otherExpenseCategoryIds.includes(entry.category_id)
      )
      || (
        entry.category_id === null
        && settings.includeUncategorizedOtherExpenses
      )
    ) {
      otherExpenses = roundMoney(otherExpenses + value);
      otherExpensesCount += 1;
    }
  }

  const cogsDetail = computeCogs(documents, range, settings);
  const grossProfit = roundMoney(revenue - cogsDetail.total - billableExpenses);
  const netProfit = roundMoney(grossProfit - otherExpenses);

  return {
    revenue,
    revenueCount,
    cogs: cogsDetail.total,
    billableExpenses,
    billableExpensesCount,
    grossProfit,
    otherExpenses,
    otherExpensesCount,
    netProfit,
    // Guarda de divisão por zero: sem receita a margem não existe (≠ 0%).
    netMargin: revenue > 0 ? (netProfit / revenue) * 100 : null,
    isEmpty:
      revenueCount === 0
      && billableExpensesCount === 0
      && otherExpensesCount === 0
      && cogsDetail.documentCount === 0,
    cogsDetail,
  };
}

// ===== Normalização das linhas do banco =====

/**
 * `timestamptz` do documento → 'YYYY-MM-DD' no calendário LOCAL.
 *
 * Fatiar a string ISO (`.slice(0, 10)`) daria a data em **UTC**, e em UTC-3 um
 * orçamento aprovado às 21h30 do dia 31 cairia no mês seguinte — o relatório
 * mudaria de mês sozinho, sem erro nenhum. `todayIsoDate` lê
 * getFullYear/Month/Date, que é o mesmo calendário das colunas DATE do
 * financeiro (armadilha #36).
 */
function competenceDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : todayIsoDate(parsed);
}

/** Data de competência de um orçamento: quando ele passou a valer. */
export function quoteCompetenceDate(row: {
  approved_at?: string | null;
  created_at?: string | null;
}): string | null {
  return competenceDate(row?.approved_at ?? row?.created_at ?? null);
}

/** Data de competência de uma OS: quando ela foi concluída (ou aberta). */
export function serviceOrderCompetenceDate(row: {
  completed_at?: string | null;
  created_at?: string | null;
}): string | null {
  return competenceDate(row?.completed_at ?? row?.created_at ?? null);
}

/**
 * Linha de `quotes` (com `quote_items` embutidos) → documento de CMV.
 *
 * Existe para que a lista de campos lidos fique num lugar só: perder
 * `unit_cost` ou `finance_entry_id` aqui zeraria o CMV ou traria o repasse de
 * volta para dentro dele — nos dois casos sem erro nenhum na tela.
 */
export function toCogsDocumentFromQuote(row: any): CogsDocument {
  return {
    kind: 'quote',
    id: String(row?.id ?? ''),
    date: quoteCompetenceDate(row ?? {}),
    status: row?.status ?? null,
    items: (row?.quote_items ?? []).map((item: any) => ({
      quantity: item?.quantity,
      unit_cost: item?.unit_cost,
      finance_entry_id: item?.finance_entry_id ?? null,
    })),
  };
}

/** Linha de `service_orders` (com `service_order_items`) → documento de CMV. */
export function toCogsDocumentFromServiceOrder(row: any): CogsDocument {
  return {
    kind: 'service_order',
    id: String(row?.id ?? ''),
    date: serviceOrderCompetenceDate(row ?? {}),
    status: row?.status ?? null,
    originQuoteId: row?.quote_id ?? null,
    items: (row?.service_order_items ?? []).map((item: any) => ({
      quantity: item?.quantity,
      unit_cost: item?.unit_cost,
    })),
  };
}
